import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from './ui/Button'
import { PAGE_SIZES } from '../api/types'

interface Props {
  total: number
  limit: number
  offset: number
  onOffsetChange: (offset: number) => void
  onPageSizeChange: (limit: number) => void
  /** Plural noun for the range label — "targets", "board entries". */
  noun?: string
  disabled?: boolean
}

/**
 * Server-side paging control, shared by the run board and the cumulative board.
 *
 * It states the range and the total ("showing 101–200 of 247") rather than a page
 * number, because the count is the thing a reader needs: a sweep no longer stops
 * at ten targets, so "page 2 of 3" does not say how many hypotheses survived
 * review. Both hooks reset the offset on a sort or filter change — an offset kept
 * across a re-ordering pages into a different set of rows than the one on screen.
 *
 * Hidden entirely when everything fits on one page: a control that only ever
 * reads "showing 1–7 of 7" is noise.
 *
 * The three controls were hand-rolled buttons with their own borders and their own
 * `disabled:` opacities; they are `ui/Button` now, which is also where the focus
 * ring comes from. Both tables call this while the redesign is in flight, so the
 * props and the offset arithmetic are unchanged on purpose.
 */
export function Pagination({
  total,
  limit,
  offset,
  onOffsetChange,
  onPageSizeChange,
  noun = 'rows',
  disabled = false,
}: Props) {
  const smallestPage = Math.min(...PAGE_SIZES)
  // Still offer the page-size control at ≤ one page when a smaller size would
  // split the set, so the choice is not stuck at whatever the default was.
  if (total <= smallestPage) return null

  const first = total === 0 ? 0 : offset + 1
  const last = Math.min(offset + limit, total)
  const hasPrev = offset > 0
  const hasNext = last < total

  return (
    <nav
      aria-label={`${noun} paging`}
      className="flex flex-wrap items-center justify-between gap-3 px-1 py-2 text-xs text-slate-400"
    >
      <div>
        Showing{' '}
        <span className="font-mono tabular-nums text-slate-200">
          {first}–{last}
        </span>{' '}
        of <span className="font-mono tabular-nums text-slate-200">{total}</span>{' '}
        {noun}
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5">
          <span>Per page</span>
          <select
            value={limit}
            disabled={disabled}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-lg border border-slate-600 bg-slate-900 px-1.5 py-1 font-mono tabular-nums text-slate-200 transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            aria-label="Previous page"
            disabled={disabled || !hasPrev}
            onClick={() => onOffsetChange(Math.max(0, offset - limit))}
          >
            <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Next page"
            disabled={disabled || !hasNext}
            onClick={() => onOffsetChange(offset + limit)}
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </nav>
  )
}
